import React, { useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Scissors, Settings, CheckCircle, X } from "lucide-react";

const Products = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const productImages = [
    "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/vzcxz4jq_IMG_2178.jpeg",
    "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/nk4w43yj_IMG_2179.jpeg",
    "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/vxjz5j7o_IMG_2182.jpeg",
    "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/ysfh9y2j_IMG_2247.jpeg",
    "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/fyfsuj0p_IMG_2246.jpeg",
    "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/3ialntzs_IMG_2185.jpeg"
  ];

  const applications = [
    {
      name: "Calzature",
      image: "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/t95nsnik_IMG_2243.jpeg"
    },
    {
      name: "Pelletteria",
      image: "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/1deha2j1_IMG_2183.jpeg"
    },
    {
      name: "Abbigliamento", 
      image: "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/zivzxyb8_IMG_2184.jpeg"
    },
    {
      name: "Guarnizioni",
      image: "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/0dt1muk4_guarnizione.jpeg"
    },
    {
      name: "Settore Automobilistico",
      image: "https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/toy06uya_auto.jpeg"
    }
  ];

  const features = [
    "Acciaio di alta qualità",
    "Produzione su misura",
    "Alto livello qualitativo",
    "Tempi di consegna rapidi",
    "Assistenza tecnica",
    "Precisione millimetrica"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Scissors className="h-16 w-16 mx-auto mb-6 text-blue-200" />
            <h1 className="text-5xl font-bold mb-6">FUSTELLE</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Realizziamo fustelle in acciaio su vostro ordine e disegno, 
              ideali per tutte le lavorazioni e per tutti i materiali
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Fustelle di Precisione
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Realizziamo fustelle in acciaio su vostro ordine e disegno, ideali per tutte 
                  le lavorazioni e per tutti i materiali rispettando sempre un alto livello qualitativo.
                </p>
                <p>
                  Produciamo fustelle, in particolare per calzature, pelletteria, abbigliamento 
                  e guarnizioni, inoltre fustelle per allestimenti interni nel settore automobilistico.
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Caratteristiche</h3>
                <div className="grid grid-cols-2 gap-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/4d0c3cch_home_con%20materiali%20di%20qualit%C3%A0.jpg"
                alt="Materiali di qualità - Fustellificio Ideal"
                className="w-full h-96 object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>

          {/* Product Gallery */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              I Nostri Prodotti
            </h2>
            <Tabs defaultValue="gallery" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="gallery">Gallery</TabsTrigger>
                <TabsTrigger value="applications">Applicazioni</TabsTrigger>
              </TabsList>
              
              <TabsContent value="gallery" className="mt-8">
                <div className="grid md:grid-cols-3 gap-6">
                  {productImages.map((image, index) => (
                    <Card key={index} className="hover:shadow-lg transition-shadow duration-300 cursor-pointer w-fit mx-auto">
                      <CardContent className="p-4">
                        <img
                          src={image}
                          alt={`Fustella ${index + 1}`}
                          className="w-auto h-48 max-w-full object-contain rounded-xl hover:scale-105 transition-transform duration-300"
                          onClick={() => setSelectedImage(image)}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="applications" className="mt-8">
                <div className="grid md:grid-cols-5 gap-6">
                  {applications.map((application, index) => (
                    <Card 
                      key={index} 
                      className="hover:shadow-lg transition-shadow duration-300 cursor-pointer transform hover:scale-105"
                      onClick={() => setSelectedApplication(application)}
                    >
                      <CardContent className="p-8 text-center">
                        <Settings className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                        <h3 className="font-semibold text-gray-900">{application.name}</h3>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Technical Specifications */}
          <div className="bg-gray-50 rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Specifiche Tecniche
              </h2>
              <p className="text-lg text-gray-600">
                Dettagli tecnici delle nostre fustelle
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Materiale</h3>
                <p className="text-gray-600">Acciaio di alta qualità</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">2</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Precisione</h3>
                <p className="text-gray-600">±0.1mm di tolleranza</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">3</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Personalizzazione</h3>
                <p className="text-gray-600">Su vostro disegno</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">4</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Consegna</h3>
                <p className="text-gray-600">Tempi rapidi</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal for application images */}
      {selectedApplication && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedApplication(null)}
        >
          <div className="relative max-w-3xl max-h-[80vh]">
            <button
              onClick={() => setSelectedApplication(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={selectedApplication.image}
              alt={`Fustelle per ${selectedApplication.name}`}
              className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
            />
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
              <h3 className="text-xl font-bold">{selectedApplication.name}</h3>
              <p className="text-sm">Fustelle specifiche per il settore {selectedApplication.name.toLowerCase()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal for enlarged images */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-full">
            <img
              src={selectedImage}
              alt="Enlarged view"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;